/** Deliberately small analytics vocabulary: never pass application objects. */
export type Surface = 'website' | 'app' | 'proof';
export const events = ['signup_clicked', 'pricing_viewed', 'contact_submitted', 'user_signed_up',
  'api_keys_viewed', 'sdk_code_copied', 'recording_created', 'session_claimed',
  'checkout_started', 'paid_conversion', 'proof_loaded', 'proof_played', 'proof_unlocked', 'operation_failed'] as const;
export type AnalyticsEvent = typeof events[number];
export type EventProperties = { operation?: 'load' | 'playback' | 'unlock' | 'checkout'; outcome?: 'success' | 'failure' };

const websiteRoutes = new Set(['/', '/pricing', '/developers', '/contact', '/privacy', '/terms', '/demo-proof', '/blog']);
const appRoutes = new Set(['/', '/sign-in', '/sign-up', '/billing', '/account', '/settings', '/settings/api-keys',
  '/integration', '/integration/sdk', '/integration/api', '/invoices', '/settings/meta', '/evidence', '/evidence/websites', '/native-evidence', '/sandbox', '/sdk', '/api']);
export function safeRoute(path: string, surface: Surface): string {
  const route = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  if (surface === 'proof') return route === '/' || route === '/proof' ? '/' : '/:proof';
  if (surface === 'website') return websiteRoutes.has(route) ? route : route.startsWith('/blog/') ? '/blog/:article' : '/:page';
  const normalized = route.replace(/^\/app(?=\/|$)/, '') || '/';
  if (/^\/sign-in(\/|$)/.test(normalized)) return '/sign-in';
  if (/^\/sign-up(\/|$)/.test(normalized)) return '/sign-up';
  if (/^\/sandbox(\/|$)/.test(normalized)) return '/sandbox';
  return appRoutes.has(normalized) ? normalized : normalized.startsWith('/evidence/') ? '/evidence/:proof' : '/:page';
}

/** One exclusion rule for recorder lifecycle and outgoing snapshot defense. */
export function replayEligible(path: string, surface: Surface): boolean {
  const route = path.split(/[?#]/)[0].replace(/^\/app(?=\/|$)/, '') || '/';
  return surface !== 'proof' && !/^\/(?:sign-in|sign-up|sandbox|integration\/sdk|native-evidence)(?:\/|$)/.test(route)
    && !safeRoute(path, surface).includes(':proof');
}

/** rrweb DOM attributes and metadata can contain secrets even when text/input masking is on. */
export function sanitizeRecording(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizeRecording);
  if (!value || typeof value !== 'object') return value;
  const output: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (key === 'href') {
      try {
        const url = new URL(String(child));
        const surface = url.hostname === 'app.verfi.io' ? 'app' : 'website';
        output[key] = ['app.verfi.io', 'verfi.io', 'www.verfi.io'].includes(url.hostname)
          ? `https://${url.hostname}${safeRoute(url.pathname, surface)}` : 'https://verfi.io/';
      } catch { output[key] = 'https://verfi.io/'; }
      continue;
    }
    if (key === 'attributes' && child && typeof child === 'object') {
      const attrs: Record<string, unknown> = {};
      for (const [name, attr] of Object.entries(child)) {
        // Layout only. No arbitrary data-*, titles, names, values, URLs or inline handlers.
        if (name === 'href' && typeof attr === 'string' && /^https:\/\/(?:app\.)?verfi\.io\/(?:_next\/static|_astro)\/[a-zA-Z0-9_./-]+\.css$/.test(attr)) attrs[name] = attr;
        if (name === 'rel' && attr === 'stylesheet') attrs[name] = attr;
        if (['class', 'type', 'role', 'width', 'height', 'rr_width', 'rr_height', 'rr_scrollLeft', 'rr_scrollTop'].includes(name)) attrs[name] = attr;
      }
      output[key] = attrs;
    } else if (key === 'textContent' || key === 'text' || key === 'value') {
      output[key] = typeof child === 'string' ? child.replace(/\S/g, '*') : child;
    } else output[key] = sanitizeRecording(child);
  }
  return output;
}
